/**
 * Все референсы подборки: пины всех её слов одним списком, без повторов (пин,
 * найденный двумя словами, — один раз, с подписью «из каких слов»). Те же
 * фильтры, выделение и теги, что на экране слова, плюс фильтр «слово».
 *
 * Смотреть и размечать может любой вошедший — и чужую подборку: тег и «в
 * библиотеку» ничего не меняют в самой подборке. Опроса задач сбора здесь нет:
 * сетка статична до перезагрузки, собирают на страницах слов.
 *
 * Ответ один на экран с потолком по сохранениям: при `truncated` честно
 * говорим, что показаны не все.
 */
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useMemo } from "react";
import { Link, useParams } from "react-router";

import { References } from "@/components/trends/References";
import { ErrorLine, Loading } from "@/components/trends/ui";
import { fittingPath } from "@/lib/fitting";
import { num } from "@/lib/media";
import { loadTopicPins, retryQuery, trendKeys, type Pin, type TopicPins } from "@/lib/trends";

export function TrendTopicRefsPage() {
  const topicId = Number(useParams().topicId);
  const valid = Number.isFinite(topicId) && topicId > 0;
  const data = useQuery({
    queryKey: trendKeys.topicPins(topicId),
    queryFn: () => loadTopicPins(topicId),
    retry: retryQuery,
    enabled: valid,
  });

  if (!valid)
    return (
      <div className="p-5">
        <ErrorLine error={new Error("Неверный адрес подборки.")} />
      </div>
    );
  if (data.isPending) return <Loading />;
  if (data.isError)
    return (
      <div className="grid gap-3 p-5">
        <Link to="/trends" className="inline-flex w-fit items-center gap-1 text-[12.5px] text-fg-muted hover:text-fg">
          <ArrowLeft size={13} />
          Все тематики
        </Link>
        <ErrorLine error={data.error} />
      </div>
    );
  return <RefsView d={data.data} />;
}

function RefsView({ d }: { d: TopicPins }) {
  const wordCount = useMemo(() => new Set(d.pins.flatMap((p) => p.type_ids ?? [])).size, [d.pins]);
  const wordName = useMemo(() => new Map(d.types.map((w) => [w.id, w.name])), [d.types]);
  // Та же библиотека, что с экрана слова: «подборка · слово», если слово одно.
  const libraryName = (pin: Pin | null) => {
    const only = pin?.type_ids?.length === 1 ? wordName.get(pin.type_ids[0]!) : undefined;
    return only ? `${d.name} · ${only}` : d.name;
  };

  return (
    <div className="grid gap-4 p-5">
      <Link
        to={`/trends/${d.id}`}
        className="inline-flex w-fit items-center gap-1 text-[12.5px] text-fg-muted hover:text-fg"
      >
        <ArrowLeft size={13} />
        {d.name}
      </Link>

      <header className="grid gap-1" data-annot="шапка референсов подборки">
        <h1 className="text-[18px] font-semibold tracking-[-0.01em] text-fg">Референсы · {d.name}</h1>
        <div className="text-[12.5px] text-fg-muted">
          <span className="tnum text-fg">{num(d.total)}</span> пинов из <span className="tnum text-fg">{num(wordCount)}</span>{" "}
          слов
        </div>
        {d.truncated ? (
          <div className="w-fit rounded-[var(--radius-ui)] bg-warn-bg px-2 py-1 text-[12px] text-warn">
            Показаны первые {num(d.pins.length)} по сохранениям из {num(d.total)}.
          </div>
        ) : null}
      </header>

      <References
        pins={d.pins}
        scope="topic"
        words={d.types}
        queryKey={trendKeys.topicPins(d.id)}
        defaultLibraryName={libraryName}
        emptyText="В подборке пока нет референсов — соберите их на страницах слов."
        fittingPath={(ids) => fittingPath({ pins: ids })}
      />
    </div>
  );
}
